// W3-R compliance: salary-TDS deposits, statutory remittances, funding check, close checklist, filing calendar.
// Mirrors backend com.kervolt.oms.compliance.dto.ComplianceDtos.

export type VoucherStatus = 'POSTED' | 'REVERSED';
export type RemittanceKind = 'PF' | 'WPPF_PARTICIPATION' | 'WPPF_WELFARE' | 'BWWF' | 'OTHER';
export const REMITTANCE_KINDS: RemittanceKind[] = ['PF', 'WPPF_PARTICIPATION', 'WPPF_WELFARE', 'BWWF', 'OTHER'];
export type TaskStatus = 'OPEN' | 'DONE' | 'NOT_APPLICABLE';
export type ClosePeriodStatus = 'OPEN' | 'IN_PROGRESS' | 'SIGNED_OFF';
export type ObligationFrequency = 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'ONE_OFF';
export const OBLIGATION_FREQUENCIES: ObligationFrequency[] = ['MONTHLY', 'QUARTERLY', 'ANNUAL', 'ONE_OFF'];

export interface SalaryTaxDeposit {
    id: string; institutionId: string; year: number; month: number; challanNumber: string; challanDate: string;
    bankAccountId: string; bankName?: string | null; amount: number; notes?: string | null; status: VoucherStatus;
    journalEntryId?: string | null; reversalJournalEntryId?: string | null; reversedDate?: string | null; reversalReason?: string | null;
}
export interface SalaryTaxDepositRequest {
    year: number; month: number; challanNumber: string; challanDate: string; bankAccountId: string; amount: number; notes?: string | null;
}
export interface SalaryTaxRegisterRow { year: number; month: number; deducted: number; deposited: number; outstanding: number; challanCount: number; }
export interface SalaryTaxSuggestion { year: number; month: number; deducted: number; deposited: number; suggested: number; note?: string | null; }

export interface StatutoryRemittance {
    id: string; institutionId: string; kind: RemittanceKind; year: number; month?: number | null; payee: string;
    referenceNumber?: string | null; remittanceDate: string; bankAccountId: string; bankName?: string | null;
    debitAccountId: string; debitAccountName?: string | null; amount: number; notes?: string | null; status: VoucherStatus;
    journalEntryId?: string | null; reversalJournalEntryId?: string | null; reversedDate?: string | null; reversalReason?: string | null;
}
export interface StatutoryRemittanceRequest {
    kind: RemittanceKind; year: number; month?: number | null; payee: string; referenceNumber?: string | null;
    remittanceDate: string; bankAccountId: string; debitAccountId: string; amount: number; notes?: string | null;
}
export interface RemittanceSuggestion {
    kind: RemittanceKind; year: number; month?: number | null; due?: number | null; remitted: number; suggested?: number | null; note?: string | null;
}

export interface FundingCheck {
    bankAccountId?: string | null; bankName: string; asOf: string; netPayTotal: number; loansApprovedNotDisbursed: number;
    required: number; bankBalance?: number | null; odHeadroom?: number | null; available?: number | null; gap?: number | null; status: 'OK' | 'SHORT'; notes: string[];
}

export interface TemplateTask {
    id: string; defaultKey?: string | null; title: string; ownerUserId?: string | null; ownerRole?: string | null;
    dueDayOffset: number; required: boolean; linkPath?: string | null; sequence: number; ownerName?: string | null;
}
export interface TemplateTaskRequest {
    title: string; ownerUserId?: string | null; ownerRole?: string | null; dueDayOffset: number; required: boolean;
    linkPath?: string | null; sequence: number;
}
export interface LoadDefaultsResult { created: number; skipped: number; }
export interface CloseTask {
    id: string; closePeriodId: string; title: string; ownerUserId?: string | null; ownerRole?: string | null;
    dueDate?: string | null; required: boolean; linkPath?: string | null; sequence: number; status: TaskStatus;
    doneBy?: string | null; doneAt?: string | null; note?: string | null; ownerName?: string | null;
}
export interface ClosePeriod {
    id: string; institutionId: string; year: number; month: number; status: ClosePeriodStatus; reopened: boolean;
    signedOffBy?: string | null; signedOffAt?: string | null; reopenedBy?: string | null; reopenedAt?: string | null;
    reopenReason?: string | null; tasksTotal: number; tasksFinished: number; requiredOpen: number; tasks: CloseTask[];
}

export interface Obligation {
    id: string; code: string; title: string; authority?: string | null; frequency: ObligationFrequency;
    daysAfterPeriodEnd?: number | null; dueMonth?: number | null; dueDay?: number | null; oneOffDate?: string | null;
    ownerUserId?: string | null; ownerName?: string | null; remind: boolean; enabled: boolean; citation?: string | null; verifyMarker: boolean;
}
export interface ObligationRequest {
    code: string; title: string; authority?: string | null; frequency: ObligationFrequency;
    daysAfterPeriodEnd?: number | null; dueMonth?: number | null; dueDay?: number | null; oneOffDate?: string | null;
    ownerUserId?: string | null; remind: boolean; enabled: boolean; citation?: string | null; verifyMarker: boolean;
}
export interface ComplianceTask {
    id: string; obligationId: string; obligationCode?: string | null; title: string; authority?: string | null;
    periodStart: string; periodEnd: string; dueDate: string; status: TaskStatus; ownerUserId?: string | null;
    doneBy?: string | null; doneAt?: string | null; note?: string | null; linkedDocumentType?: string | null;
    linkedDocumentId?: string | null; overdue: boolean; verifyMarker: boolean;
}
